import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Spin } from "antd";
import { useNavigate, useLocation } from "react-router-dom";

import AuthContext from "../../contexts/AuthContext";
import { setClarityUserContext } from "../../utils/telemetry";

import useAuthentication from "./useAuthentication";
import useToken from "./useToken";
import useUser from "./useUser";

const Auth = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [oauthProcessing, setOauthProcessing] = useState(false);
  const redirectHandled = useRef(false);

  const { token, updateToken, clearToken, ensureValidToken } = useToken();

  const handleTokenExpired = () => {
    clearToken();
    navigate("/login");
  };

  const {
    handleLogin,
    completeLogin,
    msalInstance,
    loading: loadingUserAuth,
  } = useAuthentication(({ user, token }) => {
    setUser(user);
    updateToken(token);
    navigate(location.pathname === "/login" ? "/" : location.pathname);
  });

  const { user, setUser, fetchUser, loading } = useUser(
    ensureValidToken,
    handleTokenExpired
  );

  const logout = () => {
    clearToken();
    setUser(null);
    navigate("/login");
  };

  useEffect(() => {
    if (redirectHandled.current) return;
    redirectHandled.current = true;

    const handleRedirect = async () => {
      try {
        const response = await msalInstance.handleRedirectPromise();

        if (response && response.accessToken) {
          setOauthProcessing(true);
          await completeLogin(response);
          setOauthProcessing(false);

          return;
        }
      } catch (error) {
        console.error("OAuth redirect handling failed:", error);
        setOauthProcessing(false);
      }

      if (token) {
        fetchUser();
      } else {
        navigate("/login");
      }
    };

    handleRedirect();
  }, []);

  useEffect(() => {
    if (user) {
      setClarityUserContext({
        adminEmail: user.email,
        adminName: user.name,
        adminId: user.id,
      });
    }
  }, [user]);

  if (oauthProcessing) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "#fff",
        }}
      >
        <Spin size="large" />
        <p
          style={{
            margin: 0,
            marginTop: 12,
            fontSize: 16,
            color: "#333",
          }}
        >
          Signing you in...
        </p>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        updateToken,
        logout,
        handleLogin,
        loading: loadingUserAuth || loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

Auth.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Auth;
